import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import ThemeToggle from './ThemeToggle';
import { useModal } from '../../contexts/ModalContext';
import { servicesData } from '../../data/servicesData';
import { industriesData } from '../../data/industriesData';

import WeatherWidget from './WeatherWidget';

const Header = () => {
  const { openConsultation } = useModal();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(() => location.pathname.startsWith('/services/'));
  const [isMobileIndustriesOpen, setIsMobileIndustriesOpen] = useState(() => location.pathname.startsWith('/industries/'));
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    setIsMobileServicesOpen(location.pathname.startsWith('/services/'));
    setIsMobileIndustriesOpen(location.pathname.startsWith('/industries/'));
  }, [location.pathname]);

  const navigationItems = [
    { name: 'Home', path: '/', icon: 'Home' },
    { name: 'About', path: '/about-mission-values', icon: 'Users' },
    { name: 'Technology', path: '/technology-showcase-interactive-capabilities', icon: 'Code' },
    { name: 'Team', path: '/team-excellence-expertise-personality', icon: 'UserCheck' },
    { name: 'Contact', path: '/contact-engagement-hub', icon: 'MessageCircle' }
  ];

  const secondaryItems = [
    { name: 'Locations', path: '/locations', icon: 'MapPin' },
    { name: 'AI Assistant', path: '/ai-assistant-interface-intelligent-support', icon: 'Bot' },
    { name: 'Global Collaboration', path: '/global-collaboration', icon: 'Globe' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'bg-background/95 backdrop-blur-md shadow-2xl border-b border-primary/20'
        : 'bg-background/90 backdrop-blur-sm'
        }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-20 px-6 lg:px-8">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center group relative"
            onClick={closeMenu}
          >
            <div className="absolute -inset-2 bg-gradient-to-r from-primary/20 to-accent/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <img
              src="/assets/bytepairs-logo.png"
              alt="Byte Pairs Logo"
              className="h-14 w-14 rounded-full border border-primary/20 object-cover relative z-10 transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-[0_0_20px_rgba(234,179,8,0.5)]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${isActivePath(item.path)
                  ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-[0_0_20px_rgba(234,179,8,0.4)]'
                  : 'text-text-secondary hover:text-primary hover:bg-muted border border-transparent hover:border-primary/30'
                  }`}
              >
                <Icon name={item.icon} size={16} />
                <span>{item.name}</span>
              </Link>
            ))}

            {/* Services Dropdown */}
            <div className="relative group">
              <button className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${location.pathname.startsWith('/services/')
                ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-[0_0_20px_rgba(234,179,8,0.4)]'
                : 'text-text-secondary hover:text-primary hover:bg-muted border border-transparent hover:border-primary/30'
                }`}>
                <Icon name="Layers" size={16} />
                <span>Services</span>
                <Icon name="ChevronDown" size={12} className="ml-1 transition-transform group-hover:rotate-180" />
              </button>

              <div className="absolute top-full left-0 mt-2 w-72 bg-popover border border-primary/30 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 overflow-hidden z-50">
                <div className="py-2">
                  {Object.values(servicesData).map((srv) => (
                    <Link
                      key={srv.id}
                      to={`/services/${srv.id}`}
                      className={`flex items-center space-x-3 px-5 py-3 text-sm font-medium transition-colors duration-200 ${isActivePath(`/services/${srv.id}`)
                        ? 'bg-primary/10 text-primary font-bold'
                        : 'text-popover-foreground hover:text-primary hover:bg-muted'
                        }`}
                    >
                      <Icon name={srv.icon} size={16} className="text-primary" />
                      <div>
                        <div className="font-bold">{srv.title}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Industries Dropdown */}
            <div className="relative group">
              <button className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${location.pathname.startsWith('/industries/')
                ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-[0_0_20px_rgba(234,179,8,0.4)]'
                : 'text-text-secondary hover:text-primary hover:bg-muted border border-transparent hover:border-primary/30'
                }`}>
                <Icon name="Briefcase" size={16} />
                <span>Industries</span>
                <Icon name="ChevronDown" size={12} className="ml-1 transition-transform group-hover:rotate-180" />
              </button>

              <div className="absolute top-full left-0 mt-2 w-72 bg-popover border border-primary/30 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 overflow-hidden z-50">
                <div className="py-2 max-h-96 overflow-y-auto">
                  {Object.values(industriesData).map((ind) => (
                    <Link
                      key={ind.id}
                      to={`/industries/${ind.id}`}
                      className={`flex items-center space-x-3 px-5 py-3 text-sm font-medium transition-colors duration-200 ${isActivePath(`/industries/${ind.id}`)
                        ? 'bg-success/10 text-success font-bold'
                        : 'text-popover-foreground hover:text-primary hover:bg-muted'
                        }`}
                    >
                      <Icon name={ind.icon} size={16} className="text-success" />
                      <div>
                        <div className="font-bold">{ind.title}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* More Menu */}
            <div className="relative group">
              <button className="flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-text-secondary hover:text-primary hover:bg-muted border border-transparent hover:border-primary/30 transition-all duration-300">
                <Icon name="MoreHorizontal" size={16} />
                <span>More</span>
              </button>

              <div className="absolute top-full right-0 mt-2 w-56 bg-popover border border-primary/30 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 overflow-hidden">
                {secondaryItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-3 px-5 py-4 text-sm font-semibold transition-all duration-300 ${isActivePath(item.path)
                      ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground'
                      : 'text-popover-foreground hover:text-primary hover:bg-muted'
                      }`}
                  >
                    <Icon name={item.icon} size={16} />
                    <span>{item.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <WeatherWidget />
            <ThemeToggle />
            <Button
              variant="outline"
              size="sm"
              iconName="Calendar"
              iconPosition="left"
              className="border-primary/40 text-primary hover:bg-primary/10 hover:border-primary font-semibold transition-all duration-300"
              onClick={openConsultation}
            >
              Free Consultation
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="ArrowRight"
              iconPosition="right"
              className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-primary-foreground font-bold shadow-[0_0_20px_rgba(234,179,8,0.4)] hover:shadow-[0_0_30px_rgba(234,179,8,0.6)] transition-all duration-300"
              onClick={openConsultation}
            >
              Start Project
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2.5 rounded-xl text-text-secondary hover:text-primary hover:bg-muted border border-transparent hover:border-primary/30 transition-all duration-300"
            aria-label="Toggle menu"
          >
            <Icon name={isMenuOpen ? "X" : "Menu"} size={24} />
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden transition-all duration-300 ease-in-out ${isMenuOpen
            ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
            }`}
        >
          <div className="px-6 py-4 bg-background/95 backdrop-blur-md border-t border-primary/20">
            <nav className="space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMenu}
                  className={`flex items-center space-x-3 px-5 py-3.5 rounded-xl text-sm font-semibold transition-all duration-300 ${isActivePath(item.path)
                    ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-[0_0_15px_rgba(234,179,8,0.3)]'
                    : 'text-text-secondary hover:text-primary hover:bg-muted border border-transparent hover:border-primary/30'
                    }`}
                >
                  <Icon name={item.icon} size={18} />
                  <span>{item.name}</span>
                </Link>
              ))}

              {/* Mobile Services Accordion */}
              <div>
                <button
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className={`w-full flex items-center justify-between px-5 py-3.5 rounded-xl text-sm font-semibold transition-all duration-300 ${location.pathname.startsWith('/services/')
                    ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-[0_0_15px_rgba(234,179,8,0.3)]'
                    : 'text-text-secondary hover:text-primary hover:bg-muted border border-transparent hover:border-primary/30'
                    }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="Layers" size={18} />
                    <span>Services</span>
                  </div>
                  <Icon name={isMobileServicesOpen ? "ChevronUp" : "ChevronDown"} size={16} />
                </button>
                {isMobileServicesOpen && (
                  <div className="pl-8 space-y-1 mt-1">
                    {Object.values(servicesData).map((srv) => (
                      <Link
                        key={srv.id}
                        to={`/services/${srv.id}`}
                        onClick={closeMenu}
                        className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${isActivePath(`/services/${srv.id}`)
                          ? 'bg-primary/10 text-primary font-bold'
                          : 'text-text-secondary hover:text-primary'
                          }`}
                      >
                        <Icon name={srv.icon} size={14} className="text-primary" />
                        <span>{srv.title}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Industries Accordion */}
              <div>
                <button
                  onClick={() => setIsMobileIndustriesOpen(!isMobileIndustriesOpen)}
                  className={`w-full flex items-center justify-between px-5 py-3.5 rounded-xl text-sm font-semibold transition-all duration-300 ${location.pathname.startsWith('/industries/')
                    ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-[0_0_15px_rgba(234,179,8,0.3)]'
                    : 'text-text-secondary hover:text-primary hover:bg-muted border border-transparent hover:border-primary/30'
                    }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="Briefcase" size={18} />
                    <span>Industries</span>
                  </div>
                  <Icon name={isMobileIndustriesOpen ? "ChevronUp" : "ChevronDown"} size={16} />
                </button>
                {isMobileIndustriesOpen && (
                  <div className="pl-8 space-y-1 mt-1 max-h-60 overflow-y-auto">
                    {Object.values(industriesData).map((ind) => (
                      <Link
                        key={ind.id}
                        to={`/industries/${ind.id}`}
                        onClick={closeMenu}
                        className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${isActivePath(`/industries/${ind.id}`)
                          ? 'bg-success/10 text-success font-bold'
                          : 'text-text-secondary hover:text-primary'
                          }`}
                      >
                        <Icon name={ind.icon} size={14} className="text-success" />
                        <span>{ind.title}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {secondaryItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMenu}
                  className={`flex items-center space-x-3 px-5 py-3.5 rounded-xl text-sm font-semibold transition-all duration-300 ${isActivePath(item.path)
                    ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-[0_0_15px_rgba(234,179,8,0.3)]'
                    : 'text-text-secondary hover:text-primary hover:bg-muted border border-transparent hover:border-primary/30'
                    }`}
                >
                  <Icon name={item.icon} size={18} />
                  <span>{item.name}</span>
                </Link>
              ))}
            </nav>

            {/* Mobile CTA Buttons */}
            <div className="mt-6 space-y-3">
              <div className="flex justify-center mb-4 space-x-4">
                <WeatherWidget />
                <ThemeToggle />
              </div>
              <Button
                variant="outline"
                fullWidth
                iconName="Calendar"
                iconPosition="left"
                className="border-primary/40 text-primary hover:bg-primary/10 hover:border-primary font-semibold"
                onClick={() => { closeMenu(); openConsultation(); }}
              >
                Free Consultation
              </Button>
              <Button
                variant="default"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-primary-foreground font-bold shadow-[0_0_20px_rgba(234,179,8,0.4)]"
                onClick={() => { closeMenu(); openConsultation(); }}
              >
                Start Project
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;